'use client';

import { LoadingFallback, SessionExpiredNotice, Toaster } from '@order-up/common';
import { keepPreviousData, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { getAppCheckHealth } from '../firebase/appCheck';
import { initialize, type FirestoreCacheMode } from '../firebase/config';
import { useAuthSubscription } from '../hooks/useAuth';
import { useSessionKeepAlive } from '../hooks/useSessionKeepAlive';
import { useSessionHealth } from '../hooks/useSessionHealth';
import { usePersistedQueryCache } from './queryPersistence';
import {
  getUserFriendlyMessage,
  errorHandler,
  markUserNotified,
  setUserNotifier,
} from '../utils/errorHandler';
import { isDevelopment } from '../utils/env';
import { createLogger } from '../utils/logger';

const logger = createLogger('AppProviders');

/**
 * A Firestore `permission-denied` that is most likely a transient App Check
 * cold-start (the reCAPTCHA Enterprise token hasn't minted yet), rather than a
 * genuine rules denial. Gating on App Check health means once a token has
 * successfully minted (`healthy`), a real permission-denied is NOT retried.
 */
function isAppCheckColdStart(error: any): boolean {
  if (error?.code !== 'permission-denied') return false;
  const health = getAppCheckHealth();
  // `refused` is not a cold start: the SDK will not mint again on this page,
  // so retrying only postponed the error state by up to a minute.
  return health !== 'healthy' && health !== 'refused';
}

/**
 * Handles the authentication subscription, keeps the server session cookie
 * alive, and restores the persisted slice of the query cache. Rendered only
 * after Firebase has been initialized.
 */
const AuthSubscription = ({ queryClient }: { queryClient: QueryClient }) => {
  useAuthSubscription();
  // Nothing else re-mints the session cookie: `refreshSessionCookie` was only
  // ever called reactively from the apiClient's 401 interceptor, so the cookie
  // expired on its own and the next load bounced the user through the auth app.
  useSessionKeepAlive();
  // Repeat visits paint boot-critical data from cache and revalidate behind it.
  usePersistedQueryCache(queryClient);
  return null; // This component does not render anything.
};

/**
 * Renders the dead-session notice for every OrderUp app.
 *
 * Mounted here rather than per-app because the condition is global: the server
 * session cookie and the Firebase sign-in expire on DIFFERENT clocks, so any
 * long-lived tab can reach "looks signed in, every server write rejected".
 * Before this, that state was reported only as a console warning from
 * `useSessionKeepAlive` and the operator saw nothing but downstream failures —
 * in production on 2026-09-06, an admin save spinning on "Updating…" forever.
 */
const SessionExpiredGate = () => {
  const { expired } = useSessionHealth();
  return (
    <SessionExpiredNotice
      expired={expired}
      onReload={() => {
        if (typeof window !== 'undefined') window.location.reload();
      }}
    />
  );
};


/**
 * Where a user-facing error message actually becomes pixels.
 *
 * 🔴 REGISTERED AT MODULE SCOPE, NOT IN AN EFFECT. `handleError` can run before
 * any component mounts (a failing bootstrap, an early subscription), and a
 * notifier registered in `useEffect` would not exist yet — which is the silent
 * failure this whole change removes, reintroduced one layer up.
 *
 * `toast.error` returns an id, and handing it back lets a later, more specific
 * message REPLACE the generic one React Query already showed instead of
 * stacking a second toast on top of it.
 */
setUserNotifier(
  (message) => toast.error(message),
  (id) => toast.dismiss(id),
);

export interface AppProvidersProps {
  children: React.ReactNode;
  /**
   * Whether to show React Query DevTools.
   * Defaults to false in production, true in development.
   */
  showDevTools?: boolean;
  /**
   * Component shown while Firebase initializes — the FIRST thing a user sees
   * on a cold load. Defaults to the shared, theme-aware, spinner-free
   * `LoadingFallback` shell. Each app SHOULD pass a `loadingComponent` that
   * mirrors its real layout (its app-shell skeleton) so the boot is seamless
   * — the same skeleton then carries through the auth/selection gate.
   */
  loadingComponent?: React.ReactNode;
  /**
   * Custom QueryClient configuration.
   * If not provided, uses default configuration.
   */
  queryClient?: QueryClient;
  /**
   * Custom component to show when initialization fails (e.g. Firebase config missing).
   * Receives error and retry callback. If not provided, a default error + retry UI is shown.
   */
  initErrorComponent?: (props: { error: Error; retry: () => void }) => React.ReactNode;
  /**
   * Pre-populate the query cache before the first render.
   *
   * Called inside the same lazy `useState` initializer that creates the
   * QueryClient, so seeded entries are present on render #1 — a `useEffect`
   * would land one commit too late and every gate would already have decided
   * to fetch.
   *
   * Intended for server-rendered boot data (see
   * `apps/management/src/lib/server/ssrBootstrap.ts`). Seeds must set a
   * `staleTime` rather than pose as `initialData`, so React Query still
   * revalidates in the background: SSR is a head start, never a second source
   * of truth.
   *
   * Optional, and absent in five of the six apps — behaviour is byte-identical
   * when it is not passed.
   */
  seedQueryCache?: (queryClient: QueryClient) => void;
  /**
   * Render the real page in the SERVER HTML instead of `loadingComponent`.
   *
   * Without it, Firebase initialises in an effect, so the server HTML and the
   * first client render are both the loading shell: a public page reaches the
   * browser as a skeleton, and nothing on it — not even its hero photo — starts
   * loading until the JS has downloaded, hydrated and re-rendered. On a phone
   * that is the "images take a while to show" on every restaurant site.
   *
   * With it, the server renders `children` (they must not touch Firebase
   * during render — only in effects/queries, as React Query and the auth hooks
   * already do), and the browser initialises Firebase synchronously during
   * the FIRST client render so that render matches the server's.
   *
   * For public, server-rendered pages. Signed-in app shells keep the gate.
   */
  ssr?: boolean;
  /**
   * Where Firestore keeps what this app reads. Defaults to `persistent`.
   * Back-office apps (management, admin, billing, crm) MUST pass `memory` —
   * see {@link FirestoreCacheMode}.
   */
  firestoreCache?: FirestoreCacheMode;
}

/**
 * Runs the Firebase initializer.
 *
 * Supabase is NOT initialised here. Every browser reader goes through
 * `getSupabaseClient()`, which creates the client on first use, so an eager
 * call bought nothing — but it made `@supabase/supabase-js` (~870 KB of source)
 * a static dependency of every app that mounts these providers, including the
 * guest checkout, which has no Supabase env and never reads it (measured
 * 2026-09-28).
 * @returns null on success, Error on Firebase failure
 */
function runInitializers(firestoreCache: FirestoreCacheMode | undefined): Error | null {
  try {
    initialize({ firestoreCache });
  } catch (error) {
    return error instanceof Error ? error : new Error(String(error));
  }
  return null;
}

/**
 * Common application providers for all OrderUp platform apps.
 *
 * Includes:
 * - Firebase initialization (required; shows error + retry on failure)
 * - (Supabase is lazy — `getSupabaseClient()` creates it on first use)
 * - React Query setup with DevTools
 * - Authentication subscription
 *
 * @example
 * ```tsx
 * // In your app's root layout or _app.tsx
 * import { AppProviders } from '@order-up/common/providers';
 *
 * export default function RootLayout({ children }) {
 *   return (
 *     <html>
 *       <body>
 *         <AppProviders>{children}</AppProviders>
 *       </body>
 *     </html>
 *   );
 * }
 * ```
 */
export function AppProviders({
  children,
  showDevTools = isDevelopment(),
  loadingComponent = <LoadingFallback />,
  queryClient: customQueryClient,
  initErrorComponent,
  seedQueryCache,
  ssr = false,
  firestoreCache,
}: AppProvidersProps) {
  const [queryClient] = useState(() => {
    const client = customQueryClient || new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
        refetchOnWindowFocus: false, // Prevent unnecessary refetches when switching tabs
        // Keep the last successful data on screen while a query refetches or
        // its key changes (e.g. switching restaurant). Without this, every
        // refetch/invalidation flips `isLoading` true and components gated on
        // it blank out to a full-page skeleton — the "everything reloads the
        // whole page" feel. With it, the old data stays visible and only
        // `isFetching` toggles, so updates land in place. Industry-standard
        // default for list/detail UIs.
        placeholderData: keepPreviousData,
        retry: (failureCount, error: any) => {
          // App Check cold-start race: the first Firestore reads on a fresh
          // load can beat the reCAPTCHA Enterprise token mint and come back as
          // a transient `permission-denied`. While App Check hasn't reported
          // healthy yet, retry those a few extra times — the token warms within
          // a few seconds, so the data lands without a manual reload. Once App
          // Check IS healthy, a genuine permission-denied is left to fail.
          if (isAppCheckColdStart(error)) {
            return failureCount < 5;
          }
          // Refused by App Check: every retry on this page is refused too
          // (the SDK blocks re-attestation for 24 h), so fail at once and let
          // the page show its error state and reload offer.
          if (error?.code === 'permission-denied' && getAppCheckHealth() === 'refused') {
            return false;
          }
          // Don't retry on 4xx errors. Axios surfaces the HTTP status at
          // `error.response.status`; only fall back to `error.status` for the
          // rare error shape that carries it at the top level.
          const httpStatus = error?.response?.status ?? error?.status;
          if (httpStatus >= 400 && httpStatus < 500) {
            return false;
          }
          return failureCount < 3;
        },
        retryDelay: (attemptIndex, error: any) => {
          // Steady ~1.5s spacing while waiting out the App Check cold start
          // (reCAPTCHA mint latency); exponential backoff for everything else.
          if (isAppCheckColdStart(error)) {
            return 1500;
          }
          return Math.min(1000 * 2 ** attemptIndex, 30000);
        },
      },
      mutations: {
        retry: false, // Don't retry mutations by default
        onError: (error, _variables, context) => {
          // Global error handling for mutations
          // Log the error (and send to Sentry if configured)
          errorHandler.handleError(error, {
            component: 'ReactQuery',
            operation: 'mutation',
            metadata: { context }
          });

          // Mutations that render their own inline error UI stamp
          // `_skipGlobalToast = true` on the rejected error to prevent
          // double-feedback (toast on top of inline `RichErrorAlert`).
          // The destructive-action hook (`useDestructiveMutation`) sets
          // this flag automatically; non-dialog mutations leave the
          // flag unset and continue to surface the friendly toast.
          if ((error as { _skipGlobalToast?: boolean })?._skipGlobalToast === true) {
            return;
          }

          // Show user-friendly toast
          // Components can override this with their own onError.
          //
          // The id is recorded against the error so that a component which
          // ALSO reports it (`handleError(..., { showToUser: true })`) replaces
          // this generic line instead of stacking a second toast on it. This
          // handler runs first, before the component's `catch` resumes.
          markUserNotified(error, toast.error(getUserFriendlyMessage(error)));
        },
      },
    },
    });

    // Seed BEFORE the client is handed to the provider, so the very first
    // render already sees the data. A throwing seeder must not take the app
    // down — a failed head start is still a working app.
    if (seedQueryCache) {
      try {
        seedQueryCache(client);
      } catch (error) {
        logger.warn('Query cache seeding failed — continuing without it', {
          error: error instanceof Error ? error.message : String(error),
        });
      }
    }

    return client;
  });

  // `ssr`: initialise during the first CLIENT render (lazy initializer), so
  // that render shows `children` exactly like the server HTML did. The server
  // itself never initialises the client SDK — `children` render without it.
  const [initError, setInitError] = useState<Error | null>(() =>
    ssr && typeof window !== 'undefined' ? runInitializers(firestoreCache) : null,
  );
  const [isInitialized, setIsInitialized] = useState(() => ssr && initError === null);

  const runInit = React.useCallback(() => {
    setInitError(null);
    const err = runInitializers(firestoreCache);
    if (err) {
      logger.error('Firebase initialization failed', { error: err.message });
      setInitError(err);
      return;
    }
    setIsInitialized(true);
    // `firestoreCache` is fixed per app root; initialize() is idempotent after
    // the first call, so a later change could not re-configure Firestore anyway.
  }, [firestoreCache]);

  useEffect(() => {
    runInit();
  }, [runInit]);

  const defaultInitErrorUI = initError && (
    <div style={{ padding: 24, textAlign: 'center', fontFamily: 'system-ui, sans-serif' }}>
      <p style={{ marginBottom: 8, color: '#b91c1c' }}>Failed to initialize</p>
      <p style={{ marginBottom: 16, fontSize: 14, color: '#6b7280' }}>
        {initError.message}
      </p>
      <button
        type="button"
        onClick={runInit}
        style={{
          padding: '8px 16px',
          backgroundColor: '#0d9488',
          color: 'white',
          border: 'none',
          borderRadius: 6,
          cursor: 'pointer',
          fontSize: 14
        }}
      >
        Retry
      </button>
    </div>
  );

  return (
    <QueryClientProvider client={queryClient}>
      {initError ? (
        initErrorComponent ? initErrorComponent({ error: initError, retry: runInit }) : defaultInitErrorUI
      ) : isInitialized ? (
        <>
          <AuthSubscription queryClient={queryClient} />
          <SessionExpiredGate />
          {children}
        </>
      ) : (
        loadingComponent
      )}
      {/*
        Single, centralized sonner Toaster for every OrderUp app. Mounted here
        — inside QueryClientProvider but OUTSIDE the init-gate — so toasts
        (including the global mutation toast above) render even while Firebase
        is initializing or has failed. ThemeProvider is a parent (see
        ClientProviders), so the Toaster's useTheme() resolves correctly.
        Per-app <Toaster /> mounts were removed to prevent double-mount /
        duplicate toasts; do NOT re-add a Toaster in any app layout/main.
      */}
      <Toaster />
      {showDevTools && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}
